/**
 * Labstep
 *
 * @module components/Entity/Action/Share
 * @desc Share an entity using a ShareLink
 */

import { Can } from 'labstep-web/components/Entity/Can';
import { Action } from 'labstep-web/components/Entity/Can/types';
import FolderManagerPermission from 'labstep-web/components/Folder/Manager/Permission';
import { PermissionManager } from 'labstep-web/components/Permission/Manager';
import { ShareLinkActionCreate } from 'labstep-web/components/ShareLink/Action/Create';
import { ShareLinkInfo } from 'labstep-web/components/ShareLink/Info';
import { Limits, LimitTypes } from 'labstep-web/constants/limits';
import { ActionComponentModalContainer as ActionComponent } from 'labstep-web/core/Action/Component';
import { Flex } from 'labstep-web/core/Flex';
import { ModalDefaultContainer as Modal } from 'labstep-web/core/Modal/Default';
import { useHasExceededLimit } from 'labstep-web/hooks/limits/limits';
import { ExperimentWorkflow } from 'labstep-web/models/experiment-workflow.model';
import { Group } from 'labstep-web/models/group.model';
import { ProtocolCollection } from 'labstep-web/models/protocol-collection.model';
import { User } from 'labstep-web/models/user.model';
import { getHumanReadableEntityName } from 'labstep-web/services/i18n.service';
import type React from 'react';
import type { EntityActionShareProps } from './types';

export const EntityActionShare: React.FC<EntityActionShareProps> = ({
  entity,
  actionComponentProps,
}) => {
  const showUserLimitWarning = useHasExceededLimit(
    entity instanceof Group ? entity.user_count : -1,
    LimitTypes.MAX_USERS_PER_GROUP,
  );

  const entityDisplayName = getHumanReadableEntityName(
    entity instanceof Group ? User.entityName : '',
    false,
    true,
  );

  return (
    <Can
      entityName={entity.entityName}
      id={entity.idAttr}
      action={Action.share}
    >
      <Modal
        size="small"
        header={
          showUserLimitWarning
            ? `${entityDisplayName} Limit Reached`
            : `Share ${getHumanReadableEntityName(
                entity.entityName,
                false,
                true,
              )}`
        }
        headerColor={showUserLimitWarning ? 'red' : undefined}
        viewComponent={({ toggleModal }) => (
          <ActionComponent
            type={showUserLimitWarning ? 'button' : 'option'}
            onClick={toggleModal}
            icon="share alternate"
            text="Share"
            {...actionComponentProps}
          />
        )}
        content={
          showUserLimitWarning ? (
            <div>
              <p>
                You cannot invite more users as there is a limit of{' '}
                {Limits[LimitTypes.MAX_USERS_PER_GROUP]} users per
                workspace on the Free Tier.
              </p>
              <p>
                Contact{' '}
                <a href="mailto:info@labstep.com">info@labstep.com</a>{' '}
                to upgrade to Academic Plus for up to 50 users or
                Academic Institute for unlimited users.
              </p>
            </div>
          ) : (
            <ShareLinkActionCreate parentEntity={entity as any}>
              {({ shareLink }) => (
                <Flex spacing="gap" column>
                  <ShareLinkInfo
                    shareLink={shareLink}
                    parentEntity={entity as any}
                  />
                  {entity.entityName !== Group.entityName && (
                    <PermissionManager parentEntity={entity as any} />
                  )}
                  {(entity.entityName ===
                    ExperimentWorkflow.entityName ||
                    entity.entityName ===
                      ProtocolCollection.entityName) && (
                    <FolderManagerPermission
                      permissionParent={
                        entity as
                          | ExperimentWorkflow
                          | ProtocolCollection
                      }
                    />
                  )}
                </Flex>
              )}
            </ShareLinkActionCreate>
          )
        }
        actions={[]}
      />
    </Can>
  );
};
